import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, Flag, Play, Plus } from "lucide-react";

import { sportsService, type Competition, type Match, type Team } from "../../services/sportsService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { EVENT_TYPES, MATCH_TYPES, clock, day, isOpenMatch, labelFrom, matchConfig, matchStatus, scoreLine, tallyGoals } from "./sportsLogic";

function ScheduleForm({ competitions, teams, onClose, onDone }: { competitions: Competition[]; teams: Team[]; onClose: () => void; onDone: (row: Match) => void }) {
  const open = competitions.filter((row) => row.status === "scheduled" || row.status === "in_progress");
  const [competitionId, setCompetitionId] = useState(open.length === 1 ? String(open[0].id) : "");
  const [home, setHome] = useState("");
  const competition = open.find((row) => String(row.id) === competitionId);
  const eligible = teams.filter((row) => row.is_active && row.sport === competition?.sport);
  const today = todayIso();
  const save = useMutation({ mutationFn: sportsService.createMatch, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      competition: Number(competitionId),
      match_type: text("match_type"),
      home_team: Number(home),
      away_team: Number(text("away")),
      match_date: text("date"),
      match_time: text("time"),
      venue: text("venue"),
      referee: text("referee"),
    });
  };
  const minDate = competition ? (competition.start_date > today ? competition.start_date : today) : today;
  return (
    <FormModal
      id="fixture"
      title="Schedule a match"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The match could not be scheduled.") : undefined}
      saveLabel="Schedule it"
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The fixture" divided={false}>
        <Field label="Competition" className="sm:col-span-2" hint={competition ? `${competition.sport_name}, ${day(competition.start_date)} to ${day(competition.end_date)}.` : open.length ? undefined : "Create a competition first, even a friendly one."}>
          <select required value={competitionId} onChange={(event) => { setCompetitionId(event.target.value); setHome(""); }} className="input mt-1">
            <option value="">Choose a competition</option>
            {open.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.sport_name}</option>)}
          </select>
        </Field>
        <Field label="Home">
          <select required value={home} disabled={!competition} onChange={(event) => setHome(event.target.value)} className="input mt-1">
            <option value="">Choose a team</option>
            {eligible.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Away" hint={competition && eligible.length < 2 ? `Only ${eligible.length} active ${competition.sport_name} team; add the visiting team under Teams.` : undefined}>
          <select required name="away" disabled={!competition} className="input mt-1">
            <option value="">Choose a team</option>
            {eligible.filter((row) => String(row.id) !== home).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Stage">
          <select name="match_type" defaultValue="friendly" className="input mt-1">
            {MATCH_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Venue" optional>
          <input name="venue" maxLength={200} defaultValue={competition?.venue ?? ""} className="input mt-1" placeholder="e.g. School main pitch" />
        </Field>
        <Field label="Date">
          <input required type="date" name="date" min={minDate} max={competition?.end_date} defaultValue={minDate} className="input mt-1" />
        </Field>
        <Field label="Kick-off">
          <input required type="time" name="time" defaultValue="15:00" className="input mt-1" />
        </Field>
        <Field label="Referee" optional className="sm:col-span-2">
          <input name="referee" maxLength={100} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** One match, from kick-off to the final whistle: events, the result, or calling it off. */
function MatchDetails({ match, competition, canManage, onClose, onChanged, setNotice }: {
  match: Match;
  competition?: Competition;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
  setNotice: (notice: NoticeState) => void;
}) {
  const today = todayIso();
  const queryClient = useQueryClient();
  const events = useQuery({ queryKey: ["sports", "match-events", match.id], queryFn: () => sportsService.getEventsFor(match.id) });
  const homeSquad = useQuery({ queryKey: ["sports", "squad", match.home_team], queryFn: () => sportsService.getTeamMembers(match.home_team), enabled: canManage });
  const awaySquad = useQuery({ queryKey: ["sports", "squad", match.away_team], queryFn: () => sportsService.getTeamMembers(match.away_team), enabled: canManage });
  const squad = [...(homeSquad.data ?? []), ...(awaySquad.data ?? [])].filter((row) => row.is_active);
  const tally = tallyGoals(match, events.data ?? [], squad);
  const [finishing, setFinishing] = useState(false);
  const [result, setResult] = useState<{ home: string; away: string } | null>(null);
  const [duration, setDuration] = useState("90");
  const [notes, setNotes] = useState("");
  const [eventForm, setEventForm] = useState(0);
  const [newDate, setNewDate] = useState(match.match_date > today ? match.match_date : today);
  const [newTime, setNewTime] = useState(clock(match.match_time));
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) onClose();
    onChanged();
  };
  const start = useMutation({ mutationFn: sportsService.startMatch, onSuccess: () => done(`${match.home_team_name} v ${match.away_team_name} has kicked off.`) });
  const addEvent = useMutation({
    mutationFn: sportsService.createMatchEvent,
    onSuccess: () => {
      setEventForm((count) => count + 1);
      queryClient.invalidateQueries({ queryKey: ["sports", "match-events", match.id] });
    },
  });
  const complete = useMutation({ mutationFn: sportsService.completeMatch, onSuccess: (row) => done(`Result recorded: ${scoreLine(row)}.`, true) });
  const postpone = useMutation({ mutationFn: sportsService.postponeMatch, onSuccess: (row) => done(`Moved to ${day(row.match_date)} at ${clock(row.match_time)}.`) });
  const cancel = useMutation({ mutationFn: sportsService.cancelMatch, onSuccess: () => done("The match was cancelled.", true) });
  const competitionStarted = competition?.status === "in_progress";
  const score = result ?? { home: String(tally.home), away: String(tally.away) };

  return (
    <DetailsModal
      size="3xl"
      title={`${match.home_team_name} v ${match.away_team_name}`}
      headline={{
        label: match.status === "completed" ? "Result" : "Kick-off",
        value: match.status === "completed" ? `${match.home_score}–${match.away_score}` : `${match.match_date === today ? "Today" : day(match.match_date)} ${clock(match.match_time)}`,
        note: `${match.competition_name} · ${labelFrom(MATCH_TYPES, match.match_type)}${match.venue ? ` · ${match.venue}` : ""}`,
      }}
      status={matchStatus(match, today)}
      facts={[
        ["Winner", match.status === "completed" ? match.winner_name || "Draw" : undefined],
        ["Played for", match.duration_minutes ? `${match.duration_minutes} minutes` : undefined],
        ["Referee", match.referee ?? undefined],
        ["Competition", competition ? `${competition.name} (${competition.status.replace("_", " ")})` : match.competition_name],
      ]}
      sections={[
        { title: "Match notes", body: match.notes },
        { title: "Why it was postponed", body: match.postponement_reason, tone: "warning" },
        { title: "Why it was cancelled", body: match.cancellation_reason, tone: "danger" },
      ]}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          {canManage && (match.status === "scheduled" || match.status === "postponed") && (
            <button type="button" disabled={!competitionStarted || start.isPending} title={competitionStarted ? undefined : "Start the competition first, under Competitions."} onClick={() => start.mutate(match.id)} className="btn btn-primary gap-1.5">
              <Play className="h-4 w-4" /> Kick-off
            </button>
          )}
          {canManage && match.status === "in_progress" && !finishing && (
            <button type="button" onClick={() => setFinishing(true)} className="btn btn-primary gap-1.5">
              <Flag className="h-4 w-4" /> Final whistle
            </button>
          )}
        </>
      }
    >
      {canManage && (match.status === "scheduled" || match.status === "postponed") && !competitionStarted && (
        <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">{match.competition_name} has not started yet: start it under Competitions before kick-off.</p>
      )}
      {start.isError && errorLine(start.error, "The match could not start.")}

      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Match events</h4>
        {events.data?.length ? (
          <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
            {events.data.map((row) => (
              <li key={row.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-10 text-right tabular-nums text-gray-500">{row.minute}′</span>
                <span className="font-medium text-gray-900">{labelFrom(EVENT_TYPES, row.event_type)}</span>
                <span className="text-gray-700">{row.player_name}</span>
                {row.description && <span className="text-gray-500">· {row.description}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1.5 text-sm text-gray-500">{events.isLoading ? "Loading…" : "Nothing recorded."}</p>
        )}
      </section>

      {canManage && match.status === "in_progress" && !finishing && (
        <ActionPanel title="Record an event" description="Goals and penalties count towards each scorer's total when the result is recorded.">
          <form
            key={eventForm}
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              addEvent.mutate({ match: match.id, player: Number(data.get("player")), event_type: String(data.get("event_type")), minute: Number(data.get("minute")), description: String(data.get("description") ?? "").trim() || null });
            }}
            className="grid gap-3 sm:grid-cols-[1fr_1.4fr_5rem] sm:items-end"
          >
            <Field label="What">
              <select name="event_type" defaultValue="goal" className="input mt-1">
                {EVENT_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </Field>
            <Field label="Who">
              <select required name="player" className="input mt-1">
                <option value="">Choose a player</option>
                {[match.home_team, match.away_team].map((team) => (
                  <optgroup key={team} label={team === match.home_team ? match.home_team_name : match.away_team_name}>
                    {squad.filter((row) => row.team === team).map((row) => <option key={row.id} value={row.id}>{row.jersey_number ? `${row.jersey_number}. ` : ""}{row.student_name}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>
            <Field label="Minute">
              <input required type="number" name="minute" min={0} max={200} className="input mt-1 tabular-nums" />
            </Field>
            <Field label="Detail" optional className="sm:col-span-2">
              <input name="description" className="input mt-1" />
            </Field>
            <button disabled={addEvent.isPending} className="btn btn-outline gap-1.5">
              <Plus className="h-4 w-4" /> Add
            </button>
          </form>
          {addEvent.isError && errorLine(addEvent.error, "The event could not be recorded.")}
        </ActionPanel>
      )}

      {canManage && match.status === "in_progress" && finishing && (
        <ActionPanel title="Final result" description="Filled in from the goals recorded; correct it if something was missed.">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={match.home_team_name}>
              <input type="number" min={0} value={score.home} onChange={(event) => setResult({ ...score, home: event.target.value })} className="input mt-1 tabular-nums" />
            </Field>
            <Field label={match.away_team_name}>
              <input type="number" min={0} value={score.away} onChange={(event) => setResult({ ...score, away: event.target.value })} className="input mt-1 tabular-nums" />
            </Field>
            <Field label="Minutes played">
              <input type="number" min={1} value={duration} onChange={(event) => setDuration(event.target.value)} className="input mt-1 tabular-nums" />
            </Field>
          </div>
          <Field label="Notes" optional as="div">
            <AIAssistField
              rows={2}
              value={notes}
              onChange={(next) => setNotes(next)}
              placeholder="How it went, for the record and the newsletter."
              context={{ key: "sports.note", params: { kind: "match_notes", id: match.id, home_score: score.home, away_score: score.away } }}
              draftLabel="Help me write it"
              draftHint="Jot what happened; it writes it up without inventing any of it."
            />
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFinishing(false)} className="btn btn-outline">Back</button>
            <button
              type="button"
              disabled={complete.isPending || score.home === "" || score.away === ""}
              onClick={() => complete.mutate({ id: match.id, data: { home_score: Number(score.home), away_score: Number(score.away), duration_minutes: Number(duration) || 90, notes: notes.trim() } })}
              className="btn btn-primary"
            >
              Record the result
            </button>
          </div>
          {complete.isError && errorLine(complete.error, "The result could not be recorded.")}
        </ActionPanel>
      )}

      {canManage && (match.status === "scheduled" || match.status === "postponed") && (
        <ReasonPanel
          title="Postpone it"
          label="Why"
          placeholder="e.g. The pitch is waterlogged."
          context={{ key: "sports.note", params: { kind: "match_postpone", id: match.id } }}
          button="Postpone to this date"
          busy={postpone.isPending}
          error={postpone.error}
          errorText="The match could not be postponed."
          onSubmit={(reason) => postpone.mutate({ id: match.id, data: { reason, match_date: newDate, match_time: newTime } })}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="New date">
              <input type="date" min={today} max={competition?.end_date} value={newDate} onChange={(event) => setNewDate(event.target.value)} className="input mt-1" />
            </Field>
            <Field label="New kick-off">
              <input type="time" value={newTime} onChange={(event) => setNewTime(event.target.value)} className="input mt-1" />
            </Field>
          </div>
        </ReasonPanel>
      )}
      {canManage && isOpenMatch(match) && (
        <ReasonPanel
          title="Cancel the match"
          placeholder="e.g. The visiting school withdrew."
          context={{ key: "sports.note", params: { kind: "match_cancel", id: match.id } }}
          button="Cancel the match"
          danger
          busy={cancel.isPending}
          error={cancel.error}
          errorText="The match could not be cancelled."
          onSubmit={(reason) => cancel.mutate({ id: match.id, reason })}
        />
      )}
    </DetailsModal>
  );
}

/** Every fixture and result. */
export function FixturesPanel({ matches, competitions, teams, isLoading, isError, onRetry, canManage, onChanged }: {
  matches?: Match[];
  competitions: Competition[];
  teams: Team[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const columns: RegisterColumn<Match>[] = [
    { header: "Date", cell: (row) => (row.match_date === today ? "Today" : day(row.match_date)), exportValue: (row) => day(row.match_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Time", cell: (row) => clock(row.match_time), exportValue: (row) => clock(row.match_time), className: "tabular-nums text-gray-700" },
    { header: "Fixture", cell: (row) => <span className="font-medium text-gray-900">{scoreLine(row)}</span>, exportValue: (row) => scoreLine(row), className: "min-w-[14rem]" },
    { header: "Competition", cell: (row) => row.competition_name, exportValue: (row) => row.competition_name, className: "text-gray-700" },
    { header: "Stage", cell: (row) => labelFrom(MATCH_TYPES, row.match_type), exportValue: (row) => labelFrom(MATCH_TYPES, row.match_type), className: "text-gray-700" },
    { header: "Venue", cell: (row) => row.venue || "—", exportValue: (row) => row.venue ?? "", className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...matchStatus(row, today)} />, exportValue: (row) => matchStatus(row, today).label },
  ];

  const match = viewing !== null ? matches?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={matches ? "loaded" : "loading"}
        title="Fixtures and results"
        description={canManage ? "Open a match to kick off, record goals and cards, and enter the result." : "When and where the teams play, and how they got on."}
        actions={canManage && (
          <button type="button" onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Schedule a match
          </button>
        )}
        rows={matches}
        config={matchConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        openLabel={(row) => (!canManage ? "Open" : row.status === "in_progress" ? "Live" : isOpenMatch(row) && row.match_date <= today ? "Kick-off" : "Open")}
        noun={["match", "matches"]}
        exportName={{ file: "fixtures-results", title: "Fixtures and Results" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No matches yet. Create a competition, then schedule its matches." : "No matches scheduled."}
        initialState={(matches ?? []).some((row) => isOpenMatch(row) && row.match_date >= today) ? undefined : { view: "all" }}
      />
      {match && (
        <MatchDetails
          key={`${match.id}-${match.status}`}
          match={match}
          competition={competitions.find((row) => row.id === match.competition)}
          canManage={canManage}
          onClose={() => setViewing(null)}
          onChanged={onChanged}
          setNotice={setNotice}
        />
      )}
      {adding && (
        <ScheduleForm
          competitions={competitions}
          teams={teams}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            setNotice({ tone: "success", text: `${saved.home_team_name} v ${saved.away_team_name} scheduled for ${day(saved.match_date)} at ${clock(saved.match_time)}.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
